import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Package, Search, Download, Loader2, Edit2, Save, X, History, FileText, Eye, Plus, Upload, RotateCcw, Wrench } from 'lucide-react';
import { stockAPI, branchAPI, productAPI } from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import Pagination from '../ui/Pagination';
import ItemHistoryDialog from '../ui/ItemHistoryDialog';
import { toast } from 'sonner';

const statusColors = {
  'In Stock': 'bg-green-100 text-green-700',
  'Below Minimum': 'bg-amber-100 text-amber-700',
  'Above Maximum': 'bg-blue-100 text-blue-700',
  'Out of Stock': 'bg-red-100 text-red-700',
};

const StockModule = () => {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };

  const [historyProduct, setHistoryProduct] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [stockLabel, setStockLabel] = useState('');
  const [labelCounts, setLabelCounts] = useState({ out_of_stock: 0, below_minimum: 0, above_maximum: 0 });

  // Manual Adjustment state
  const [showAdjReasonDialog, setShowAdjReasonDialog] = useState(false);
  const [adjReason, setAdjReason] = useState('');
  const [adjCount, setAdjCount] = useState(0);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjPage, setAdjPage] = useState(1);
  const [adjTotal, setAdjTotal] = useState(0);
  const [adjTotalPages, setAdjTotalPages] = useState(1);

  // Create Manual Adjustment form
  const [showCreateAdj, setShowCreateAdj] = useState(false);
  const [adjProductSearch, setAdjProductSearch] = useState('');
  const [adjProductResults, setAdjProductResults] = useState([]);
  const [adjSelectedProduct, setAdjSelectedProduct] = useState(null);
  const [adjBranch, setAdjBranch] = useState('');
  const [adjCurrentQty, setAdjCurrentQty] = useState(null);
  const [adjDirection, setAdjDirection] = useState('+');
  const [adjQty, setAdjQty] = useState('');
  const [adjComment, setAdjComment] = useState('');
  const [adjSubmitting, setAdjSubmitting] = useState(false);
  const [adjFetchingStock, setAdjFetchingStock] = useState(false);

  // Import Opening Stock state
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);

  // Bulk Set Opening Stock state (Admin only)
  const [showBulkSet, setShowBulkSet] = useState(false);
  const [bulkSetFile, setBulkSetFile] = useState(null);
  const [bulkSetRevertAdj, setBulkSetRevertAdj] = useState(true);
  const [bulkSetting, setBulkSetting] = useState(false);
  const [bulkSetResult, setBulkSetResult] = useState(null);
  const bulkSetFileRef = useRef(null);

  // Export state
  const [showExport, setShowExport] = useState(false);
  const [exportBranch, setExportBranch] = useState('');
  const [exporting, setExporting] = useState(false);

  // Current user role (for Admin-only actions like Revert / Set Opening Stock)
  const _currentUser = (() => { try { return JSON.parse(localStorage.getItem('prismoline_user') || '{}'); } catch { return {}; } })();
  const isAdmin = _currentUser.role === 'Admin';
  const [repairingLinks, setRepairingLinks] = useState(false);

  // Set Opening Stock dialog (Admin only)
  const [showSetOpeningDialog, setShowSetOpeningDialog] = useState(false);
  const [setOpeningProduct, setSetOpeningProduct] = useState('');
  const [setOpeningBranch, setSetOpeningBranch] = useState('');
  const [setOpeningQty, setSetOpeningQty] = useState('');
  const [setOpeningReason, setSetOpeningReason] = useState('');
  const [setOpeningSaving, setSetOpeningSaving] = useState(false);
  const [setOpeningProductSearch, setSetOpeningProductSearch] = useState('');
  const [setOpeningProductOptions, setSetOpeningProductOptions] = useState([]);

  useEffect(() => {
    if (!showSetOpeningDialog || setOpeningProductSearch.length < 2) {
      setSetOpeningProductOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await productAPI.getAll({ search: setOpeningProductSearch, page_size: 10 });
        setSetOpeningProductOptions(res.items || res || []);
      } catch (err) {
        setSetOpeningProductOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [setOpeningProductSearch, showSetOpeningDialog]);

  const handleSetOpeningStock = async () => {
    if (!setOpeningProduct || !setOpeningBranch || !setOpeningReason.trim()) {
      toast.error('Product, Branch and Reason are all required');
      return;
    }
    const q = parseFloat(setOpeningQty);
    if (Number.isNaN(q) || q < 0) {
      toast.error('Opening quantity must be a non-negative number');
      return;
    }
    try {
      setSetOpeningSaving(true);
      const result = await stockAPI.setOpeningStock({
        product_id: setOpeningProduct,
        branch_id: setOpeningBranch,
        opening_quantity: q,
        reason: setOpeningReason.trim(),
      });
      toast.success(result.message || 'Opening stock updated');
      setShowSetOpeningDialog(false);
      setSetOpeningProduct(''); setSetOpeningBranch(''); setSetOpeningQty(''); setSetOpeningReason(''); setSetOpeningProductSearch('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set opening stock');
    } finally {
      setSetOpeningSaving(false);
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (branchFilter) params.branch = branchFilter;
      if (search) params.search = search;
      if (stockLabel) params.stock_label = stockLabel;

      const [stockData, branchesData, adjCountData] = await Promise.all([
        stockAPI.getConsolidated(params),
        branchAPI.getAll().catch(() => []),
        stockAPI.getManualAdjustmentsCount().catch(() => ({ count: 0 })),
      ]);

      setItems(stockData.data || []);
      setTotal(stockData.total || 0);
      setTotalPages(stockData.pages || 1);
      setBranches(branchesData);
      if (stockData.label_counts) setLabelCounts(stockData.label_counts);
      setAdjCount(adjCountData.count || 0);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, branchFilter, search, stockLabel]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchAdjustments = useCallback(async () => {
    try {
      setAdjLoading(true);
      const data = await stockAPI.getManualAdjustments({ page: adjPage, page_size: 50 });
      setAdjustments(data.items || []);
      setAdjTotal(data.total || 0);
      setAdjTotalPages(data.pages || 1);
    } catch { /* ignore */ } finally { setAdjLoading(false); }
  }, [adjPage]);

  useEffect(() => { if (showAdjustments) fetchAdjustments(); }, [showAdjustments, fetchAdjustments]);

  // Product search for Create Adjustment
  useEffect(() => {
    if (adjProductSearch.length < 2) { setAdjProductResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await productAPI.getAll({ search: adjProductSearch, page_size: 8 });
        const items = Array.isArray(data) ? data : (data?.items || []);
        setAdjProductResults(items);
      } catch { setAdjProductResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [adjProductSearch]);

  // Fetch current stock when product + branch selected.
  // CRITICAL: must use `physical` (NOT `available`) — the backend adjustment endpoint
  // computes delta against the physical balance. If we showed `available` here
  // (physical − awaiting-invoice reservations), the user's mental subtraction
  // would silently wipe out the reserved stock too. Bug evidence: MAJ0048 where
  // available=1100 but physical=8900, user typed "−1100" intending to land at 0
  // available, backend interpreted it as setting physical to 0 → −8900 deduction.
  useEffect(() => {
    if (!adjSelectedProduct || !adjBranch) { setAdjCurrentQty(null); return; }
    const fetchStock = async () => {
      setAdjFetchingStock(true);
      try {
        const data = await stockAPI.batchNetQty(adjBranch, [adjSelectedProduct.id]);
        const physical = data.details?.[adjSelectedProduct.id]?.physical_balance;
        const fallback = data.stock?.[adjSelectedProduct.id];
        setAdjCurrentQty(physical ?? fallback ?? 0);
      } catch { setAdjCurrentQty(0); }
      finally { setAdjFetchingStock(false); }
    };
    fetchStock();
  }, [adjSelectedProduct, adjBranch]);

  const adjFinalQty = adjCurrentQty !== null && adjQty !== ''
    ? Math.round((adjCurrentQty + (adjDirection === '+' ? 1 : -1) * parseFloat(adjQty || 0)) * 10000) / 10000
    : null;

  const handleCreateAdjSubmit = async () => {
    if (!adjSelectedProduct || !adjBranch || !adjQty || !adjComment.trim()) return;
    try {
      setAdjSubmitting(true);
      const result = await stockAPI.adjustQuantity({
        product_id: adjSelectedProduct.id,
        branch_id: adjBranch,
        new_quantity: adjFinalQty,
        reason: adjComment.trim(),
      });
      toast.success(`${result.adjustment_number}: Stock adjusted successfully`);
      setShowCreateAdj(false);
      setAdjSelectedProduct(null); setAdjProductSearch(''); setAdjBranch('');
      setAdjCurrentQty(null); setAdjQty(''); setAdjComment(''); setAdjDirection('+');
      fetchAdjustments();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Adjustment failed');
    } finally { setAdjSubmitting(false); }
  };

  const handleImportStock = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      setImportResult(null);
      const result = await stockAPI.importOpeningStock(importFile);
      setImportResult(result);
      if (result.created > 0) {
        toast.success(`Imported ${result.created} stock entries for ${result.products_matched} products`);
        fetchAll();
      } else {
        toast.error(`Import completed but 0 stock entries created. ${result.products_matched} products matched, ${result.products_unmatched} unmatched. Check details below.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await stockAPI.downloadOpeningStockTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opening_stock_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded — fill in your data and re-upload');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleBulkSet = async () => {
    if (!bulkSetFile) return;
    try {
      setBulkSetting(true);
      setBulkSetResult(null);
      const result = await stockAPI.bulkSetOpeningStock(bulkSetFile, bulkSetRevertAdj);
      setBulkSetResult(result);
      const totalChanged = (result.pairs_updated || 0) + (result.pairs_created || 0);
      if (totalChanged > 0) {
        toast.success(`Bulk-set ${totalChanged} SKU+branch pair(s). Reverted ${result.adjustments_reverted || 0} adjustment(s).`);
        fetchAll();
        fetchAdjustments();
      } else {
        toast.error('Bulk-set finished but 0 pairs were changed. Check the unmatched list below.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk-set failed');
    } finally {
      setBulkSetting(false);
    }
  };

  const startEdit = (item) => {
    setEditingRow(item.product_id + '_' + item.branch_id);
    setEditForm({
      product_id: item.product_id,
      branch_id: item.branch_id,
      sku: item.sku || '',
      category: item.category || '',
      unit: item.unit || '',
      hsn_code: item.hsn_code || '',
      gst_rate: item.gst_rate || 18,
      min_stock: item.min_stock || 0,
      max_stock: item.max_stock || 0,
      selling_price: item.selling_price || 0,
      cost_price: item.cost_price || 0,
      net_quantity: item.net_quantity || 0,
      _original_qty: item.net_quantity || 0,
    });
  };

  const saveEdit = async () => {
    // If quantity changed, show reason dialog first
    if (editForm.net_quantity !== editForm._original_qty && !showAdjReasonDialog) {
      setShowAdjReasonDialog(true);
      setAdjReason('');
      return;
    }

    try {
      setSaving(true);
      // Save product fields
      await stockAPI.updateProductFields(editForm);
      // If quantity was changed, adjust stock with reason
      if (editForm.net_quantity !== editForm._original_qty) {
        const adjResult = await stockAPI.adjustQuantity({
          product_id: editForm.product_id,
          branch_id: editForm.branch_id,
          new_quantity: editForm.net_quantity,
          reason: adjReason,
        });
        toast.success(adjResult.adjustment_number + ': ' + (adjResult.message || 'Stock adjusted'));
      } else {
        toast.success('Product fields updated');
      }
      setEditingRow(null);
      setShowAdjReasonDialog(false);
      setAdjReason('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      // Fetch ALL consolidated stock data (no pagination limit) directly from the same endpoint the UI uses
      const params = { page: 1, page_size: 9999 };
      if (exportBranch) params.branch = exportBranch;
      const stockData = await stockAPI.getConsolidated(params);
      const allItems = stockData.data || [];

      if (allItems.length === 0) {
        toast.error('No stock data found to export');
        setExporting(false);
        return;
      }

      // Build CSV client-side from the same data the UI displays
      const headers = ["SKU", "Product Name", "Category", "UOM", "HSN", "GST %", "Branch", "Inward Qty", "Dispatched Qty", "Net Stock", "Reserved (Awaiting Invoice)", "Available", "Cost Price", "Stock Value", "Status"];
      const csvRows = [headers.join(",")];
      allItems.forEach(item => {
        const row = [
          `"${(item.sku || '').replace(/"/g, '""')}"`,
          `"${(item.product_name || '').replace(/"/g, '""')}"`,
          `"${(item.category || '').replace(/"/g, '""')}"`,
          `"${(item.unit || '').replace(/"/g, '""')}"`,
          `"${(item.hsn_code || '').replace(/"/g, '""')}"`,
          item.gst_rate || 18,
          `"${(item.branch_name || '').replace(/"/g, '""')}"`,
          item.total_inward || 0,
          item.total_dispatched || 0,
          item.net_quantity || 0,
          item.reserved_qty || 0,
          item.available_qty ?? (item.net_quantity || 0),
          item.cost_price || 0,
          item.stock_value || 0,
          `"${(item.status || '').replace(/"/g, '""')}"`,
        ];
        csvRows.push(row.join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const branchName = exportBranch ? branches.find(b => b.id === exportBranch)?.name || 'Branch' : 'All';
      a.download = `Stock_Inventory_${new Date().toISOString().slice(0, 10)}_${branchName}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allItems.length} stock items`);
      setShowExport(false);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  const isEditing = (item) => editingRow === item.product_id + '_' + item.branch_id;

  return (
    <div className="p-5 lg:p-6 space-y-4" data-testid="stock-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Stock Inventory</h1>
          <p className="text-[11px] text-gray-500">Consolidated view — one line per product per branch</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="set-opening-btn" onClick={() => setShowSetOpeningDialog(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              title="Reset opening stock for a single SKU+branch (Admin only)">
              <RotateCcw className="w-3.5 h-3.5" /> Set Opening Stock
            </button>
          )}
          {isAdmin && (
            <button data-testid="bulk-set-opening-btn" onClick={() => { setShowBulkSet(true); setBulkSetFile(null); setBulkSetResult(null); }}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
              title="Bulk reset opening stock via Excel for many SKU+branch pairs (Admin only)">
              <Upload className="w-3.5 h-3.5" /> Bulk Set Opening Stock
            </button>
          )}
          {isAdmin && (
            <button data-testid="repair-product-links-btn" onClick={async () => {
                if (!window.confirm('Scan all stock rows and repair missing/inactive product links? Safe and idempotent.')) return;
                setRepairingLinks(true);
                try {
                  const res = await productAPI.repairStockReferences();
                  const s = res.summary || {};
                  toast.success(`Repair complete — created: ${s.created || 0}, reactivated: ${s.reactivated || 0}, matched: ${(s.matched_by_name || 0) + (s.matched_by_sku || 0)}, already OK: ${s.existing || 0}${s.ambiguous ? `, ambiguous: ${s.ambiguous}` : ''}`);
                  if (res.ambiguous_count > 0) toast.warning(`${res.ambiguous_count} item(s) need manual review: ${res.ambiguous.slice(0, 3).join(', ')}`);
                } catch (e) { toast.error(e.response?.data?.detail || 'Repair failed'); }
                setRepairingLinks(false);
              }} disabled={repairingLinks}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors disabled:opacity-60"
              title="Fix stock items that show in Inventory but can't be searched in order forms (Admin only)">
              {repairingLinks ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />} Repair Product Links
            </button>
          )}
          <button data-testid="import-stock-btn" onClick={() => { setShowImport(true); setImportFile(null); setImportResult(null); }}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Import Opening Stock
          </button>
          <button data-testid="export-stock-btn" onClick={() => setShowExport(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg transition-shadow"
            style={{ backgroundColor: '#24549c' }}>
            <Download className="w-3.5 h-3.5" /> Export Stock
          </button>
        </div>
      </div>

      {/* Export Dialog */}
      {showExport && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="export-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'DM Sans' }}>Export Stock Inventory</h3>
            <div className="space-y-3">
              <p className="text-[11px] text-blue-600 bg-blue-50 rounded-lg p-2.5">
                Exports current stock levels for all products — the same data you see on screen.
              </p>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch (optional)</label>
                <select data-testid="export-branch-select" value={exportBranch} onChange={e => setExportBranch(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                  <option value="">All Branches</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowExport(false)} className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button data-testid="confirm-export-btn" onClick={handleExport} disabled={exporting}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: '#24549c' }}>
                {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {exporting ? 'Exporting...' : 'Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Opening Stock Dialog (Admin only) */}
      {showSetOpeningDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="set-opening-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <RotateCcw className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Set Opening Stock</h3>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
              <p className="text-[10px] text-blue-900">
                💡 <strong>Use this for fixing a wrong opening stock.</strong> Unlike the inline Net Qty edit (which creates a delta and can compound), this directly sets the Opening Stock row to the exact value you enter. No compounding.
              </p>
            </div>
            <div className="space-y-2.5">
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Product (search SKU or name) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={setOpeningProductSearch}
                  onChange={e => { setSetOpeningProductSearch(e.target.value); setSetOpeningProduct(''); }}
                  placeholder="Type SKU or product name..."
                  data-testid="set-opening-product-search"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                {setOpeningProductOptions.length > 0 && !setOpeningProduct && (
                  <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow">
                    {setOpeningProductOptions.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSetOpeningProduct(p.id); setSetOpeningProductSearch(`${p.sku} — ${p.name}`); setSetOpeningProductOptions([]); }}
                        className="block w-full text-left px-3 py-1.5 text-[11px] hover:bg-amber-50 border-b border-gray-50 last:border-b-0"
                      >
                        <span className="font-mono text-purple-700">{p.sku}</span> — <span className="text-gray-700">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Branch <span className="text-red-500">*</span></label>
                <select value={setOpeningBranch} onChange={e => setSetOpeningBranch(e.target.value)}
                  data-testid="set-opening-branch"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300">
                  <option value="">Select branch...</option>
                  {(branches || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Opening Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={setOpeningQty}
                  onChange={e => setSetOpeningQty(e.target.value)}
                  placeholder="e.g. 161.66"
                  data-testid="set-opening-qty"
                  className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Reason <span className="text-red-500">*</span></label>
                <textarea
                  rows={2}
                  value={setOpeningReason}
                  onChange={e => setSetOpeningReason(e.target.value)}
                  placeholder="e.g. Wrong qty in original Excel import, audited physical stock count..."
                  data-testid="set-opening-reason"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setShowSetOpeningDialog(false)} disabled={setOpeningSaving}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button
                data-testid="set-opening-save-btn"
                onClick={handleSetOpeningStock}
                disabled={setOpeningSaving || !setOpeningProduct || !setOpeningBranch || setOpeningQty === '' || !setOpeningReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {setOpeningSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Set Opening Stock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Set Opening Stock Dialog (Admin only) */}
      {showBulkSet && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="bulk-set-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xl my-8">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <Upload className="w-4 h-4 text-orange-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Bulk Set Opening Stock</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Per-SKU absolute set — no compounding. Untouched SKUs are left intact.</p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-[11px] font-bold text-red-900 mb-1">⚠ How this differs from other imports:</p>
              <ul className="text-[10px] text-red-800 list-disc list-inside space-y-0.5">
                <li><strong>Import Opening Stock</strong> APPENDS new rows (compounds on top of existing).</li>
                <li><strong>Replace Opening Stock</strong> WIPES ALL opening rows globally — dangerous.</li>
                <li><strong>This Bulk Set</strong> sets each (SKU, branch) opening row to the EXACT qty in your Excel (in-place). SKUs not in the file are untouched.</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
              <p className="text-[10px] text-amber-800 font-semibold mb-1">Expected Excel Format:</p>
              <p className="text-[10px] text-amber-700">Col A: SKU | Col B: Name | Col D: Unit | Col J: Cost Price | Col K-R: Branch quantities</p>
              <button
                data-testid="bulk-set-download-template-btn"
                type="button"
                onClick={handleDownloadTemplate}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold text-purple-700 hover:text-purple-900 underline underline-offset-2"
              >
                <Download className="w-3 h-3" /> Download blank template (.xlsx)
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <input ref={bulkSetFileRef} type="file" accept=".xlsx,.xls" onChange={e => setBulkSetFile(e.target.files[0] || null)}
                  className="w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                  data-testid="bulk-set-file-input" />
                {bulkSetFile && <p className="text-[10px] text-green-600 mt-1">Selected: {bulkSetFile.name}</p>}
              </div>

              <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg bg-orange-50 border border-orange-200">
                <input type="checkbox" checked={bulkSetRevertAdj} onChange={e => setBulkSetRevertAdj(e.target.checked)}
                  className="mt-0.5" data-testid="bulk-set-revert-adj" />
                <div>
                  <p className="text-[11px] font-semibold text-orange-900">
                    Also revert ALL existing Manual Adjustments for SKUs in this file
                  </p>
                  <p className="text-[10px] text-orange-700 leading-snug">
                    Deletes the cascading negative MAJ stock rows for these (SKU, branch) pairs and marks the audit records as reverted. Recommended when fixing compounded-negative balances.
                  </p>
                </div>
              </label>

              <p className="text-[10px] text-gray-500">
                Reason: <span className="italic">Auto-logged as "Bulk Opening Set" with OPEN-BULK-#### serial per (SKU, branch) pair processed.</span>
              </p>
            </div>

            {bulkSetResult && (
              <div
                className={`mt-3 rounded-lg border-2 ${((bulkSetResult.pairs_updated || 0) + (bulkSetResult.pairs_created || 0)) > 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}
                data-testid="bulk-set-result"
              >
                <div className={`px-3 py-2 border-b ${((bulkSetResult.pairs_updated || 0) + (bulkSetResult.pairs_created || 0)) > 0 ? 'border-green-200 bg-green-100' : 'border-red-200 bg-red-100'}`}>
                  <p className={`text-sm font-bold ${((bulkSetResult.pairs_updated || 0) + (bulkSetResult.pairs_created || 0)) > 0 ? 'text-green-900' : 'text-red-900'}`}>
                    {((bulkSetResult.pairs_updated || 0) + (bulkSetResult.pairs_created || 0)) > 0 ? '✓ Bulk Set Complete' : '✗ Bulk Set — 0 Pairs Changed'}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${((bulkSetResult.pairs_updated || 0) + (bulkSetResult.pairs_created || 0)) > 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {bulkSetResult.message}
                  </p>
                </div>
                <div className="p-3 space-y-1.5">
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Updated</p>
                      <p className="font-bold text-gray-900 text-sm" data-testid="bulk-set-updated-count">{bulkSetResult.pairs_updated}</p>
                    </div>
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Created</p>
                      <p className="font-bold text-gray-900 text-sm" data-testid="bulk-set-created-count">{bulkSetResult.pairs_created}</p>
                    </div>
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Adj Reverted</p>
                      <p className="font-bold text-orange-700 text-sm" data-testid="bulk-set-reverted-count">{bulkSetResult.adjustments_reverted}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-600">
                    Rows processed: <strong>{bulkSetResult.rows_processed}</strong> | Skipped zero-qty cells: <strong>{bulkSetResult.pairs_skipped_zero}</strong> | Adjustment stock rows deleted: <strong>{bulkSetResult.adjustment_rows_deleted}</strong> | Branches: <strong>{(bulkSetResult.branch_names || []).length}</strong>
                  </p>
                  {bulkSetResult.products_unmatched > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-[11px] font-bold text-amber-900 mb-1">
                        Unmatched SKUs ({bulkSetResult.products_unmatched}):
                      </p>
                      <div className="max-h-24 overflow-y-auto">
                        {(bulkSetResult.unmatched_products || []).map((u, i) => (
                          <p key={i} className="text-[10px] text-amber-800 font-mono">
                            Row {u.row}: <span className="font-semibold">{u.sku}</span> — {u.name}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {(bulkSetResult.per_row || []).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[11px] font-semibold text-blue-800 cursor-pointer hover:underline">
                        View per-SKU changes ({bulkSetResult.per_row.length})
                      </summary>
                      <div className="max-h-48 overflow-y-auto mt-1 border border-gray-200 rounded p-2 bg-white">
                        {bulkSetResult.per_row.map((r, i) => (
                          <div key={i} className="mb-2 pb-2 border-b border-gray-100 last:border-0">
                            <p className="text-[10px] font-mono font-semibold text-gray-900">{r.sku} — <span className="font-normal text-gray-700">{r.product_name}</span></p>
                            {r.changes.map((c, j) => (
                              <p key={j} className="text-[10px] text-gray-600 ml-2">
                                • <span className="font-semibold">{c.branch}</span>: {c.previous_opening} → {c.new_opening} ({c.action === 'updated_existing' ? 'updated' : 'created'}) <span className="text-orange-600">[{c.adjustments_reverted} adj reverted]</span>
                              </p>
                            ))}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setShowBulkSet(false); setBulkSetFile(null); setBulkSetResult(null); }} disabled={bulkSetting}
                className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Close
              </button>
              <button
                data-testid="bulk-set-submit-btn"
                onClick={handleBulkSet}
                disabled={bulkSetting || !bulkSetFile}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
              >
                {bulkSetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {bulkSetting ? 'Processing...' : 'Run Bulk Set'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Opening Stock Dialog */}
      {showImport && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="import-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-xl my-8">
            <h3 className="text-sm font-bold mb-1" style={{ fontFamily: 'DM Sans' }}>Import Opening Stock</h3>
            <p className="text-[11px] text-gray-500 mb-4">Upload Excel file with SKU codes and branch quantities</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-[10px] text-amber-800 font-semibold mb-1">Expected Excel Format:</p>
              <p className="text-[10px] text-amber-700">Col A: SKU | Col B: Name | Col D: Unit | Col I: Selling Price | Col J: Cost Price</p>
              <p className="text-[10px] text-amber-700">Col K-R: Branch quantities (Ranchi, Siliguri, Sasaram, Patna, Bhubaneswar, Bangalore, Lucknow, Bhiwandi)</p>
              <button
                data-testid="download-template-btn"
                type="button"
                onClick={handleDownloadTemplate}
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold text-purple-700 hover:text-purple-900 underline underline-offset-2"
              >
                <Download className="w-3 h-3" /> Download blank template (.xlsx)
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <input ref={importFileRef} type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files[0] || null)}
                  className="w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                  data-testid="import-file-input" />
                {importFile && <p className="text-[10px] text-green-600 mt-1">Selected: {importFile.name}</p>}
              </div>
            </div>

            {importResult && (
              <div
                className={`mt-3 rounded-lg border-2 ${importResult.created > 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}
                data-testid="import-result"
              >
                <div className={`px-3 py-2 border-b ${importResult.created > 0 ? 'border-green-200 bg-green-100' : 'border-red-200 bg-red-100'}`}>
                  <p className={`text-sm font-bold ${importResult.created > 0 ? 'text-green-900' : 'text-red-900'}`}>
                    {importResult.created > 0 ? '✓ Import Successful' : '✗ Import Completed with 0 Entries'}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${importResult.created > 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {importResult.message}
                  </p>
                </div>
                <div className="p-3 space-y-1.5">
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Created</p>
                      <p className="font-bold text-gray-900 text-sm">{importResult.created}</p>
                    </div>
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Matched</p>
                      <p className="font-bold text-gray-900 text-sm">{importResult.products_matched} / {importResult.products_in_db}</p>
                    </div>
                    <div className="bg-white rounded p-1.5 border border-gray-200">
                      <p className="text-gray-500 text-[9px] uppercase">Branches</p>
                      <p className="font-bold text-gray-900 text-sm">{(importResult.branch_names || []).length}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-600">
                    <span className="font-semibold">Branches detected:</span> {(importResult.branch_names || []).join(', ') || 'None'}
                  </p>
                  {importResult.match_methods && (importResult.products_matched > 0) && (
                    <p className="text-[10px] text-gray-600">
                      <span className="font-semibold">Match methods:</span> Exact SKU: {importResult.match_methods.exact_sku}, Case-insensitive: {importResult.match_methods.case_insensitive_sku}, Partial: {importResult.match_methods.partial_sku}, Name: {importResult.match_methods.name_match}
                    </p>
                  )}
                  {importResult.created === 0 && importResult.products_matched === 0 && (
                    <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded">
                      <p className="text-[11px] text-red-900 font-bold mb-1">⚠ No SKUs matched the Product Master</p>
                      <p className="text-[10px] text-red-800">Your Excel SKUs do not match any of the {importResult.products_in_db} products in the master. Verify:</p>
                      <ul className="text-[10px] text-red-800 list-disc list-inside ml-1">
                        <li>SKU codes in Col A are spelled exactly as in Product Master</li>
                        <li>No extra spaces / hidden characters / line breaks</li>
                        <li>You're not uploading the unmodified sample row</li>
                      </ul>
                    </div>
                  )}
                  {importResult.created === 0 && importResult.products_matched > 0 && (
                    <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded">
                      <p className="text-[11px] text-amber-900 font-bold">⚠ Products matched but all branch quantities were 0</p>
                      <p className="text-[10px] text-amber-800">Check branch quantity columns (K–R) — make sure at least one column has a non-zero value per row.</p>
                    </div>
                  )}
                  {importResult.products_unmatched > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[11px] font-bold text-amber-900">
                          Unmatched rows ({importResult.products_unmatched}{(importResult.unmatched_products || []).length < importResult.products_unmatched ? ` — showing first ${(importResult.unmatched_products || []).length}` : ''}):
                        </p>
                        <button
                          type="button"
                          data-testid="copy-unmatched-btn"
                          onClick={() => {
                            const list = (importResult.unmatched_products || [])
                              .map(u => `Row ${u.row}\t${u.sku}\t${u.name || ''}`)
                              .join('\n');
                            navigator.clipboard.writeText('Row\tSKU\tName\n' + list)
                              .then(() => toast.success('Unmatched SKUs copied to clipboard'))
                              .catch(() => toast.error('Could not access clipboard'));
                          }}
                          className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 underline"
                        >
                          Copy unmatched SKUs
                        </button>
                      </div>
                      <div className="max-h-32 overflow-y-auto bg-white border border-amber-200 rounded">
                        <table className="w-full text-[10px]">
                          <thead className="bg-amber-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left text-amber-900 font-semibold">Row</th>
                              <th className="px-2 py-1 text-left text-amber-900 font-semibold">SKU</th>
                              <th className="px-2 py-1 text-left text-amber-900 font-semibold">Name</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(importResult.unmatched_products || []).map((u, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}>
                                <td className="px-2 py-0.5 text-gray-600">{u.row}</td>
                                <td className="px-2 py-0.5 font-mono text-red-700">{u.sku || '(empty)'}</td>
                                <td className="px-2 py-0.5 text-gray-700 truncate max-w-[180px]">{u.name || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowImport(false); setImportFile(null); setImportResult(null); }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Close</button>
              <button data-testid="confirm-import-btn" onClick={handleImportStock} disabled={importing || !importFile}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40">
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {importing ? 'Importing...' : importResult ? 'Re-Import Stock' : 'Import Stock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Label Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'out_of_stock', label: 'Out of Stock', count: labelCounts.out_of_stock, color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', activeColor: 'bg-red-600 text-white border-red-600' },
          { key: 'below_minimum', label: 'Below Minimum', count: labelCounts.below_minimum, color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', activeColor: 'bg-amber-600 text-white border-amber-600' },
          { key: 'above_maximum', label: 'Above Maximum', count: labelCounts.above_maximum, color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100', activeColor: 'bg-blue-600 text-white border-blue-600' },
        ].map(badge => (
          <button
            key={badge.key}
            data-testid={`stock-label-${badge.key}`}
            onClick={() => { setShowAdjustments(false); setStockLabel(prev => prev === badge.key ? '' : badge.key); setPage(1); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${!showAdjustments && stockLabel === badge.key ? badge.activeColor : badge.color}`}
          >
            {badge.label}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${!showAdjustments && stockLabel === badge.key ? 'bg-white/20' : 'bg-white'}`}>{badge.count}</span>
          </button>
        ))}
        <button
          data-testid="stock-label-manual-adjustment"
          onClick={() => { setShowAdjustments(prev => !prev); setStockLabel(''); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${showAdjustments ? 'bg-purple-600 text-white border-purple-600' : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'}`}
        >
          Manual Adjustment
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${showAdjustments ? 'bg-white/20' : 'bg-white'}`}>{adjCount}</span>
        </button>
        {(stockLabel || showAdjustments) && (
          <button onClick={() => { setStockLabel(''); setShowAdjustments(false); setPage(1); }} className="text-[10px] text-gray-500 hover:text-gray-700 underline ml-1">Clear filter</button>
        )}
      </div>

      {/* Filters */}
      {!showAdjustments && (
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="stock-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search SKU, product, category..." className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <select data-testid="stock-branch-filter" value={branchFilter} onChange={e => { setBranchFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
          <option value="">All Branches</option>
          {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <span className="text-[10px] text-gray-400">{total} items</span>
      </div>
      )}

      {/* Stock Table */}
      {!showAdjustments && (loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No stock items found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="stock-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">SKU Code</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Item Description</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">UOM</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">HSN</th>
                  <th className="text-center px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">GST%</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">MIN</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">MAX</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Sell Price</th>
                  <th className="text-right px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Cost Price</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Branch</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider min-w-[110px]">Net Qty</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="text-center px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-center px-2 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item, idx) => {
                  const editing = isEditing(item);
                  return (
                    <tr key={idx} className="hover:bg-gray-50/50" data-testid={`stock-row-${idx}`}>
                      <td className="px-3 py-2">
                        {editing ? (
                          <input value={editForm.sku} onChange={e => setEditForm({ ...editForm, sku: e.target.value })} className="w-20 px-1.5 py-1 text-xs border border-blue-200 rounded" />
                        ) : (
                          <span className="font-mono text-[10px] text-gray-600">{item.sku || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-xs font-medium text-gray-900">{item.product_name}</p>
                        {item.sub_category && <p className="text-[9px] text-gray-400">{item.sub_category}</p>}
                      </td>
                      <td className="px-3 py-2">
                        {editing ? (
                          <input value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="w-24 px-1.5 py-1 text-xs border border-blue-200 rounded" />
                        ) : (
                          <span className="text-xs text-gray-600">{item.category || '-'}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {editing ? (
                          <input value={editForm.unit} onChange={e => setEditForm({ ...editForm, unit: e.target.value })} className="w-12 px-1.5 py-1 text-xs border border-blue-200 rounded" />
                        ) : (
                          <span className="text-xs text-gray-600">{item.unit}</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {editing ? (
                          <input value={editForm.hsn_code} onChange={e => setEditForm({ ...editForm, hsn_code: e.target.value })} className="w-16 px-1.5 py-1 text-xs border border-blue-200 rounded font-mono" />
                        ) : (
                          <span className="text-[10px] font-mono text-gray-500">{item.hsn_code || '-'}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {editing ? (
                          <select value={editForm.gst_rate} onChange={e => setEditForm({ ...editForm, gst_rate: Number(e.target.value) })} className="w-14 px-1 py-1 text-xs border border-blue-200 rounded">
                            {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-600">{item.gst_rate}%</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {editing ? (
                          <input type="number" value={editForm.min_stock} onChange={e => setEditForm({ ...editForm, min_stock: Number(e.target.value) })} className="w-14 px-1.5 py-1 text-xs border border-blue-200 rounded text-right" />
                        ) : (
                          <span className="text-xs text-gray-600">{item.min_stock || '-'}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {editing ? (
                          <input type="number" value={editForm.max_stock} onChange={e => setEditForm({ ...editForm, max_stock: Number(e.target.value) })} className="w-14 px-1.5 py-1 text-xs border border-blue-200 rounded text-right" />
                        ) : (
                          <span className="text-xs text-gray-600">{item.max_stock || '-'}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {editing ? (
                          <input type="number" value={editForm.selling_price} onChange={e => setEditForm({ ...editForm, selling_price: Number(e.target.value) })} className="w-16 px-1.5 py-1 text-xs border border-blue-200 rounded text-right" />
                        ) : (
                          <span className="text-xs text-gray-700">{item.selling_price ? `₹${item.selling_price.toLocaleString()}` : '-'}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {editing ? (
                          <input type="number" value={editForm.cost_price} onChange={e => setEditForm({ ...editForm, cost_price: Number(e.target.value) })} className="w-16 px-1.5 py-1 text-xs border border-blue-200 rounded text-right" />
                        ) : (
                          <span className="text-xs text-gray-700">{item.cost_price ? `₹${item.cost_price.toLocaleString()}` : '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-700 font-medium">{item.branch_name}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {editing ? (
                          <input type="number" step="0.001" value={editForm.net_quantity}
                            onChange={e => setEditForm({ ...editForm, net_quantity: Number(e.target.value) })}
                            data-testid={`edit-qty-${idx}`}
                            className={`w-20 px-1.5 py-1 text-xs border rounded text-right font-bold ${editForm.net_quantity !== editForm._original_qty ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-blue-200'}`}
                          />
                        ) : (
                          <div className="flex flex-col items-end">
                            <span
                              data-testid={`stock-net-qty-${idx}`}
                              className={`text-xs font-bold ${item.net_quantity > 0 ? 'text-gray-900' : 'text-red-600'}`}
                              title={(item.reserved_qty || 0) > 0
                                ? `Net: ${item.net_quantity} ${item.unit} • Reserved (Awaiting Invoice): ${item.reserved_qty} • Available: ${item.available_qty}`
                                : `Net: ${item.net_quantity} ${item.unit}`}
                            >
                              {item.net_quantity.toLocaleString()} {item.unit}
                            </span>
                            {(item.reserved_qty || 0) > 0 && (
                              <span
                                data-testid={`stock-reserved-line-${idx}`}
                                className="text-[9px] text-amber-700 mt-0.5"
                              >
                                <span className="font-semibold">−{item.reserved_qty.toLocaleString()}</span> reserved →{' '}
                                <span className={(item.available_qty || 0) < 0 ? 'text-red-600 font-bold' : 'text-emerald-700 font-semibold'}>
                                  {(item.available_qty ?? item.net_quantity).toLocaleString()} avail
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="text-xs text-gray-600">₹{item.stock_value.toLocaleString()}</span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full ${statusColors[item.status] || 'bg-gray-100'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {editing ? (
                            <>
                              <button data-testid={`save-edit-${idx}`} onClick={saveEdit} disabled={saving}
                                className="p-1 rounded hover:bg-green-50 text-green-600" title="Save">
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => setEditingRow(null)} className="p-1 rounded hover:bg-red-50 text-red-400" title="Cancel">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button data-testid={`edit-stock-${idx}`} onClick={() => startEdit(item)}
                                className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Edit fields">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button data-testid={`history-stock-${idx}`} onClick={() => setHistoryProduct({ id: item.product_id, name: item.product_name, branchId: item.branch_id, branchName: item.branch_name })}
                                className="p-1 rounded hover:bg-purple-50 text-gray-400 hover:text-purple-600" title="View history">
                                <History className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage}
            totalItems={total} pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
        </div>
      ))}

      {/* Manual Adjustments Table */}
      {showAdjustments && (
        <div className="space-y-4">
          {/* Create Adjustment Button */}
          <div className="flex justify-end">
            <button data-testid="create-adj-btn" onClick={() => setShowCreateAdj(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">
              <Plus className="w-3.5 h-3.5" /> Create Manual Adjustment
            </button>
          </div>

          {/* Create Adjustment Form */}
          {showCreateAdj && (
            <div className="bg-white rounded-xl border border-purple-200 p-5" data-testid="create-adj-form">
              <h3 className="text-sm font-bold text-gray-900 mb-4">New Manual Adjustment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Product Search */}
                <div className="relative">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Product *</label>
                  {adjSelectedProduct ? (
                    <div className="flex items-center gap-2 px-3 py-2 border border-green-200 bg-green-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-gray-800">{adjSelectedProduct.name}</p>
                        <p className="text-[10px] text-gray-500">{adjSelectedProduct.sku} | {adjSelectedProduct.unit}</p>
                      </div>
                      <button onClick={() => { setAdjSelectedProduct(null); setAdjProductSearch(''); setAdjCurrentQty(null); }}
                        className="p-0.5 rounded hover:bg-red-50 text-red-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <>
                      <input data-testid="adj-product-search" value={adjProductSearch} onChange={e => setAdjProductSearch(e.target.value)}
                        placeholder="Search product by name or SKU..."
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200" />
                      {adjProductResults.length > 0 && (
                        <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                          {adjProductResults.map(p => (
                            <button key={p.id} onClick={() => { setAdjSelectedProduct(p); setAdjProductResults([]); setAdjProductSearch(''); }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-purple-50 border-b border-gray-50">
                              <span className="font-mono text-gray-500">{p.sku}</span> | <span className="font-medium">{p.name}</span> <span className="text-gray-400">({p.unit})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Branch */}
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Branch *</label>
                  <select data-testid="adj-branch-select" value={adjBranch} onChange={e => setAdjBranch(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200">
                    <option value="">Select Branch</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>

                {/* Current Qty */}
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Current Quantity</label>
                  <div className="px-3 py-2 text-xs border border-gray-100 bg-gray-50 rounded-lg font-bold text-gray-700">
                    {adjFetchingStock ? 'Loading...' : adjCurrentQty !== null ? `${adjCurrentQty} ${adjSelectedProduct?.unit || ''}` : 'Select product & branch'}
                  </div>
                </div>

                {/* +/- Direction + Adjustment Qty */}
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Adjustment *</label>
                  <div className="flex gap-1">
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button data-testid="adj-dir-plus" onClick={() => setAdjDirection('+')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${adjDirection === '+' ? 'bg-green-500 text-white' : 'text-gray-500 hover:text-green-600'}`}>+</button>
                      <button data-testid="adj-dir-minus" onClick={() => setAdjDirection('-')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${adjDirection === '-' ? 'bg-red-500 text-white' : 'text-gray-500 hover:text-red-600'}`}>−</button>
                    </div>
                    <input data-testid="adj-qty-input" type="number" step="0.001" min="0" value={adjQty} onChange={e => setAdjQty(e.target.value)}
                      placeholder="Qty" className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200" />
                  </div>
                </div>

                {/* Final Qty */}
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Final Quantity</label>
                  <div className={`px-3 py-2 text-xs border rounded-lg font-bold ${adjFinalQty !== null && adjFinalQty < 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-100 bg-blue-50 text-blue-800'}`}>
                    {adjFinalQty !== null ? `${adjFinalQty} ${adjSelectedProduct?.unit || ''}` : '—'}
                  </div>
                  {adjFinalQty !== null && adjFinalQty < 0 && <p className="text-[10px] text-red-500 mt-0.5">Warning: Final quantity is negative</p>}
                </div>

                {/* Reason */}
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Reason *</label>
                  <textarea data-testid="adj-comment-input" value={adjComment} onChange={e => setAdjComment(e.target.value)}
                    rows={2} placeholder="Why is this adjustment being made?"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 resize-none" />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => { setShowCreateAdj(false); setAdjSelectedProduct(null); setAdjProductSearch(''); setAdjBranch(''); setAdjCurrentQty(null); setAdjQty(''); setAdjComment(''); }}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button data-testid="adj-submit-btn" onClick={handleCreateAdjSubmit}
                  disabled={adjSubmitting || !adjSelectedProduct || !adjBranch || !adjQty || parseFloat(adjQty) === 0 || !adjComment.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {adjSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Submit Adjustment
                </button>
              </div>
            </div>
          )}

          {/* Adjustment History Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-purple-50/50">
            <h3 className="text-xs font-bold text-purple-800">Manual Adjustment History</h3>
            <p className="text-[10px] text-purple-500 mt-0.5">{adjTotal} adjustment records</p>
          </div>
          {adjLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-purple-400" /></div>
          ) : adjustments.length === 0 ? (
            <div className="text-center py-12 text-xs text-gray-400">No manual adjustments yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="adjustments-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/70">
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">MAJ #</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">SKU</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Product</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase text-right">Previous Qty</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase text-right">New Qty</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase text-right">Adjustment</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Reason</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Adjusted By</th>
                    <th className="px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((adj, i) => (
                    <tr key={adj.id || i} className={`border-b border-gray-50 hover:bg-gray-50/50 ${adj.reverted_at ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2"><span className="text-xs font-bold text-purple-700">{adj.adjustment_number}</span>{adj.reverted_at && <span className="ml-1 text-[9px] px-1 py-0.5 bg-red-100 text-red-700 rounded">REVERTED</span>}</td>
                      <td className="px-3 py-2"><span className="text-[11px] text-gray-600">{adj.created_at ? new Date(adj.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span></td>
                      <td className="px-3 py-2"><span className="text-[11px] text-gray-500 font-mono">{adj.sku || '-'}</span></td>
                      <td className="px-3 py-2"><span className="text-[11px] text-gray-800 font-medium">{(adj.product_name || '').substring(0, 35)}{(adj.product_name || '').length > 35 ? '...' : ''}</span></td>
                      <td className="px-3 py-2"><span className="text-[11px] text-gray-600">{adj.branch_name}</span></td>
                      <td className="px-3 py-2 text-right"><span className="text-[11px] text-gray-500">{adj.previous_quantity} {adj.unit}</span></td>
                      <td className="px-3 py-2 text-right"><span className="text-[11px] font-semibold text-gray-800">{adj.new_quantity} {adj.unit}</span></td>
                      <td className="px-3 py-2 text-right">
                        <span className={`text-[11px] font-bold ${adj.adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {adj.adjustment > 0 ? '+' : ''}{adj.adjustment}
                        </span>
                      </td>
                      <td className="px-3 py-2"><span className="text-[11px] text-gray-700 max-w-[200px] truncate block" title={adj.reason}>{adj.reason}</span></td>
                      <td className="px-3 py-2"><span className="text-[10px] text-gray-500">{adj.adjusted_by}</span></td>
                      <td className="px-3 py-2 text-center">
                        {!adj.reverted_at && isAdmin ? (
                          <button
                            type="button"
                            data-testid={`revert-adj-${adj.adjustment_number}`}
                            onClick={() => {
                              const reason = prompt(`Revert ${adj.adjustment_number}?\n\nThis will:\n• Delete the stock entry created by this adjustment\n• Mark the adjustment as Reverted in the audit log\n\nPlease enter a reason:`);
                              if (!reason || !reason.trim()) return;
                              stockAPI.revertAdjustment(adj.adjustment_number, reason.trim())
                                .then(r => { toast.success(r.message || 'Adjustment reverted'); fetchAdjustments(); })
                                .catch(err => toast.error(err.response?.data?.detail || 'Failed to revert'));
                            }}
                            className="text-[10px] font-semibold text-red-600 hover:text-red-800 underline"
                            title="Undo this adjustment"
                          >
                            Revert
                          </button>
                        ) : adj.reverted_at ? (
                          <span className="text-[9px] text-gray-400" title={`Reverted by ${adj.reverted_by} on ${adj.reverted_at?.slice(0,10)}`}>Reverted</span>
                        ) : (
                          <span className="text-[9px] text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {adjTotalPages > 1 && (
            <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
              <span className="text-[10px] text-gray-400">Page {adjPage} of {adjTotalPages}</span>
              <div className="flex gap-1">
                <button disabled={adjPage <= 1} onClick={() => setAdjPage(p => p - 1)} className="px-2 py-1 text-[10px] border rounded disabled:opacity-30">Prev</button>
                <button disabled={adjPage >= adjTotalPages} onClick={() => setAdjPage(p => p + 1)} className="px-2 py-1 text-[10px] border rounded disabled:opacity-30">Next</button>
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Adjustment Reason Dialog */}
      {showAdjReasonDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="adj-reason-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Stock Adjustment Reason</h3>
            <p className="text-[11px] text-gray-500 mb-3">
              Qty: <span className="font-semibold text-gray-700">{editForm._original_qty}</span> → <span className="font-bold text-amber-700">{editForm.net_quantity}</span>
              <span className={`ml-2 font-bold ${editForm.net_quantity - editForm._original_qty > 0 ? 'text-green-600' : 'text-red-600'}`}>
                ({editForm.net_quantity - editForm._original_qty > 0 ? '+' : ''}{Math.round((editForm.net_quantity - editForm._original_qty) * 10000) / 10000})
              </span>
            </p>
            <textarea
              data-testid="adj-reason-input"
              value={adjReason}
              onChange={e => setAdjReason(e.target.value)}
              placeholder="Why is this adjustment being made? (e.g., Physical count mismatch, Damaged goods, Data entry error)"
              rows={3}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 resize-none"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setShowAdjReasonDialog(false); setAdjReason(''); }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button
                data-testid="adj-reason-submit"
                onClick={saveEdit}
                disabled={saving || !adjReason.trim()}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Confirm Adjustment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item History Dialog */}
      {historyProduct && (
        <ItemHistoryDialog productId={historyProduct.id} productName={historyProduct.name} branchId={historyProduct.branchId} branchName={historyProduct.branchName} onClose={() => setHistoryProduct(null)} />
      )}
    </div>
  );
};

export default StockModule;
